import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HandHeart, Users, Trophy, Leaf } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { clinicInteriorImage, teamGroupImage } from '../mock';

const Value = ({ Icon, title, text }) => (
  <div className="rounded-2xl border border-neutral-200 p-6 hover:border-[#CA0028] transition-colors">
    <span className="w-12 h-12 rounded-2xl bg-neutral-100 grid place-items-center">
      <Icon size={18} />
    </span>
    <h3 className="mt-5 text-[18px] font-semibold text-neutral-900">{title}</h3>
    <p className="mt-2 text-[14px] leading-relaxed text-neutral-600">{text}</p>
  </div>
);

const AboutPage = () => {
  return (
    <div className="App bg-white">
      <Header />
      <PageHero
        eyebrow="About Us"
        title="A sanctuary of"
        italic="health and wellness."
        description="Since 1995, Uptown Physiotherapy Clinic has helped patients recover with evidence-informed care, clear treatment plans, and a team that listens before it treats."
        image={teamGroupImage}
        breadcrumbs={[{ label: 'Home', to: '/' }, { label: 'About' }]}
      />

      {/* Story */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div className="rounded-[24px] overflow-hidden aspect-[5/4] bg-neutral-100">
            <img src={clinicInteriorImage} alt="Uptown Physiotherapy Clinic clinic" className="w-full h-full object-cover" />
          </div>
          <div>
            <h2 className="text-[36px] lg:text-[48px] leading-[1.02] font-bold tracking-tight text-neutral-900">
              Our <span className="font-serif-italic font-normal">story</span>
            </h2>
            <div className="mt-6 space-y-5 text-[16px] leading-relaxed text-neutral-700">
              <p>
                Uptown Physiotherapy Clinic opened its doors in 1995 with a small team and a big idea:
                that great physiotherapy is built on listening. Nearly three decades later, we’re
                still asking the same first question&mdash;<em>what matters to you?</em>&mdash;before we
                write a single note.
              </p>
              <p>
                Today, our three community clinics offer a full range of services from sport rehab
                to pelvic health, guided by clinicians who invest in ongoing education and treat
                every patient as a partner.
              </p>
            </div>
            <Link to="/team" className="mt-8 inline-flex items-center gap-2 bg-[#CA0028] hover:bg-[#A80021] text-white text-[14px] font-medium pl-6 pr-2 py-2 rounded-full transition-colors">
              Meet the Team
              <span className="w-8 h-8 grid place-items-center rounded-full bg-white/15"><ArrowRight size={14} /></span>
            </Link>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="max-w-[1240px] mx-auto px-6">
          <div className="max-w-2xl">
            <span className="text-[12px] font-semibold tracking-[0.2em] text-neutral-500 uppercase">What we believe</span>
            <h2 className="mt-3 text-[36px] lg:text-[48px] leading-[1.02] font-bold tracking-tight text-neutral-900">
              Better movement builds <span className="font-serif-italic font-normal">better recovery.</span>
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            <Value Icon={HandHeart} title="Empathetic Care" text="We ask, we listen, we adapt. Every plan starts with your story." />
            <Value Icon={Trophy} title="Evidence-Informed" text="Modern techniques, guided by research and clinical experience." />
            <Value Icon={Users} title="Team Approach" text="Physios, athletic therapists, and RMTs collaborate on your care." />
            <Value Icon={Leaf} title="Long-Term Confidence" text="We equip you with the tools to stay well long after discharge." />
          </div>
        </div>
      </section>

      {/* Facility */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1240px] mx-auto px-6 grid md:grid-cols-3 gap-8">
          <div>
            <h3 className="text-[22px] font-semibold text-neutral-900">Our treatment philosophy</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-neutral-600">
              Care is evidence-informed, empathetic, and designed to help each patient understand
              their body and stay involved in the plan.
            </p>
          </div>
          <div>
            <h3 className="text-[22px] font-semibold text-neutral-900">Our facility</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-neutral-600">
              Bright, welcoming spaces with dedicated parking, 7,000 sq ft of therapy space, and
              modern rehabilitation equipment across every location.
            </p>
          </div>
          <div>
            <h3 className="text-[22px] font-semibold text-neutral-900">Our modalities</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-neutral-600">
              Our clinicians combine hands-on therapy, therapeutic exercise, education, and current
              modalities to support safer, more confident recovery.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
